import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  MessageCircle,
  Trash2,
  Edit2,
  Briefcase,
  Search,
  Star,
  BookOpen,
  ChevronDown,
  Sparkles,
  Bell,
  User,
  CreditCard,
  Menu,
  X,
  Settings,
  Shield,
  HelpCircle,
  LogOut,
  Users
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import AgentModal from './AgentModal';
import axios from 'axios';

export default function Sidebar({ 
  sessions, 
  currentSession, 
  onSelectSession, 
  onNewChat,
  onDeleteSession,
  onRenameSession,
  onAgentResult,
  onOpenCreditModal,
  user,
  creditStatus,
  unreadCount,
  onCreditsClick,
  onSubscriptionClick,
  onNotificationsClick,
  onProfileClick,
  onSidebarToggle,
  onLogout,
  onSettingsClick,
  onPrivacyClick,
  onSupportClick,
  onParentPanelClick
}) {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  
  // Filter sessions by search query
  const filteredSessions = sessionSearch
    ? sessions.filter(s => (s.title || '').toLowerCase().includes(sessionSearch.toLowerCase()))
    : sessions;
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const agentDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  const handleStartEdit = (session) => {
    setEditingId(session.id);
    setEditTitle(session.title);
  };

  const handleSaveEdit = (sessionId) => {
    if (editTitle.trim()) {
      onRenameSession(sessionId, editTitle);
    }
    setEditingId(null);
  };

  const handleAgentResult = (result) => {
    // Pass agent result to parent (ChatPage)
    if (onAgentResult) {
      onAgentResult(result);
    }
  };

  // Fetch agents when dropdown opens
  useEffect(() => {
    if (showAgentDropdown && agents.length === 0) {
      fetchAgents();
    }
  }, [showAgentDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
      }
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showAgentDropdown || showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAgentDropdown, showProfileDropdown]);

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const token = localStorage.getItem('access_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const response = await axios.get(`/api/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAgents(response.data.agents);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAgentClick = (agent) => {
    setShowAgentDropdown(false);
    // Open modal with selected agent
    setShowAgentModal(true);
  };

  return (
    <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full">
      {/* Header with Close Button */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">Z</span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">Zeha</span>
          </div>
          <button
            onClick={onSidebarToggle}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Menüyü kapat"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        {/* Yeni Sohbet */}
        <button
          onClick={onNewChat}
          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mb-2"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Yeni Sohbet</span>
        </button>

        {/* Agents Dropdown */}
        <div className="relative mb-2" ref={agentDropdownRef}>
          <button
            onClick={() => setShowAgentDropdown(!showAgentDropdown)}
            className="w-full flex items-center justify-between px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Briefcase className="w-5 h-5" />
              <span className="text-sm font-medium">Agents</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Yeni</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showAgentDropdown ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {/* Dropdown Menu */}
          {showAgentDropdown && (
            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
              {loadingAgents ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  Yükleniyor...
                </div>
              ) : agents.length > 0 ? (
                agents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => handleAgentClick(agent)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{agent.icon}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {agent.name}
                        </span>
                      </div>
                      <span className="text-xs text-blue-600 dark:text-blue-400">
                        {agent.credit_cost} kredi
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
                      {agent.description}
                    </p>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  Agent bulunamadı
                </div>
              )}
            </div>
          )}
        </div>

        {/* Ara */}
        <button
          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mb-2"
        >
          <Search className="w-5 h-5" />
          <span className="text-sm font-medium">Ara</span>
        </button>

        {/* AI Kütüphane */}
        <button
          onClick={() => navigate('/library')}
          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mb-2"
        >
          <Star className="w-5 h-5" />
          <span className="text-sm font-medium">AI Kütüphane</span>
        </button>

        {/* Study Modu - YENİ */}
        <button
          onClick={() => navigate('/study')}
          className="w-full flex items-center space-x-3 px-3 py-2 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors font-medium"
        >
          <BookOpen className="w-5 h-5" />
          <span className="text-sm">Study Modu</span>
        </button>
      </div>

      {/* Sohbet Arama + Başlık */}
      <div className="px-3 py-2 space-y-2">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-1">
          Sohbetler
        </h3>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={sessionSearch}
            onChange={(e) => setSessionSearch(e.target.value)}
            placeholder="Sohbet ara..."
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 border-none rounded-lg text-gray-700 dark:text-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto px-2">
        {filteredSessions.map((session) => (
          <div
            key={session.id}
            className={`
              group relative mb-2 rounded-lg transition-colors cursor-pointer
              ${currentSession?.id === session.id
                ? 'bg-blue-50 dark:bg-blue-900/20'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }
            `}
          >
            {editingId === session.id ? (
              <div className="p-3">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={() => handleSaveEdit(session.id)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(session.id)}
                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            ) : (
              <div
                onClick={() => onSelectSession(session)}
                className="p-3 flex items-start justify-between"
              >
                <div className="flex items-start space-x-2 flex-1 min-w-0">
                  <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
                  <span className="text-sm text-gray-900 dark:text-white truncate">
                    {session.title || 'Untitled'}
                  </span>
                </div>
                
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartEdit(session);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                  >
                    <Edit2 className="w-3 h-3 text-gray-500" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this chat?')) {
                        onDeleteSession(session.id);
                      }
                    }}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* User Actions - Bottom (Horizontal) */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-around gap-2">
          {/* Show login button only if no user at all */}
          {!user ? (
            <button
              onClick={() => {
                onProfileClick();
              }}
              className="flex flex-col items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex-1"
            >
              <User className="w-5 h-5" />
              <span className="text-xs mt-1 font-medium">Oturum Aç</span>
            </button>
          ) : (
            <>
              {/* Notifications - for all logged in users */}
              <button
                onClick={onNotificationsClick}
                className="relative flex flex-col items-center justify-center p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-1"
              >
                <Bell className="w-5 h-5" />
                <span className="text-xs mt-1">Bildirim</span>
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px]">
                    {unreadCount}
                  </Badge>
                )}
              </button>
              
              {/* Credits - for all logged in users */}
              <button
                onClick={onCreditsClick}
                className="relative flex flex-col items-center justify-center p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-1"
              >
                <Sparkles className="w-5 h-5" />
                <span className="text-xs mt-1">{creditStatus?.total_available || 0}</span>
                {/* Green Plus Badge */}
                <div className="absolute -top-1 -right-1 h-4 w-4 bg-green-500 rounded-full flex items-center justify-center">
                  <Plus className="w-3 h-3 text-white" strokeWidth={3} />
                </div>
              </button>
              
              {/* Profile with Dropdown - for all logged in users */}
              <div className="relative flex-1" ref={profileDropdownRef}>
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="w-full flex flex-col items-center justify-center p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="text-xs mt-1 truncate max-w-[60px]">{user?.name?.split(' ')[0] || 'Profil'}</span>
                </button>
            
            {/* Profile Dropdown Menu */}
            {showProfileDropdown && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 min-w-[200px]">
                {/* Guest users: Show Register/Login option */}
                {user && (user.email?.includes('guest') || user.user_type === 'guest') ? (
                  <>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        onProfileClick();
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg flex items-center gap-2 font-medium"
                    >
                      <User className="w-4 h-4" />
                      Kayıt Ol / Giriş Yap
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        onLogout();
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors last:rounded-b-lg flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Çıkış Yap
                    </button>
                  </>
                ) : (
                  <>
                    {/* Registered users: Show full menu */}
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        onProfileClick();
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Hesabım
                    </button>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    onNotificationsClick();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 relative"
                >
                  <Bell className="w-4 h-4" />
                  Bildirimler
                  {unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-[10px]">
                      {unreadCount}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    onSubscriptionClick();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <CreditCard className="w-4 h-4" />
                  Abonelik
                </button>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    onSettingsClick();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Ayarlar
                </button>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    onPrivacyClick();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Gizlilik
                </button>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    onSupportClick();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <HelpCircle className="w-4 h-4" />
                  Destek
                </button>
                
                {/* Parent Paneli - Sadece kayıtlı kullanıcılar için */}
                {user && !user.email?.includes('guest') && user.user_type !== 'guest' && (
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      onParentPanelClick();
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Parent Paneli
                  </button>
                )}
                
                <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                <button
                  onClick={() => {
                    setShowProfileDropdown(false);
                    onLogout();
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors last:rounded-b-lg flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Çıkış Yap
                </button>
                  </>
                )}
              </div>
            )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Agent Modal */}
      <AgentModal
        isOpen={showAgentModal}
        onClose={() => setShowAgentModal(false)}
        onExecute={handleAgentResult}
        onOpenCreditModal={onOpenCreditModal}
      />
    </div>
  );
}
